import {
  useAccountInfoQuery,
  useLoginMutation,
  useLogoutMutation,
  usePasswordResetCheckMutation,
  usePasswordResetEnterMutation,
  usePasswordResetRequestMutation,
  useRegisterMutation,
} from '~~/app/composables/queries/useStoreApi'
import { useAuthStore } from '~~/stores/authStore'
import { mapAccountIdentity, type AccountIdentitySource } from '~~/app/utils/accountIdentity'
import { isSessionExpiredError } from '~~/app/utils/apiErrorMessage'
import { clearCartIdentityToken } from '~~/app/composables/cart/cartIdentityToken'
import { useUIStore } from '~~/stores/uiStore'
import { useAnalytics } from '~~/app/composables/useAnalytics'
import { usePwa } from '~~/app/composables/usePwa'

export function useAuth() {
  const router = useRouter()
  const authStore = useAuthStore()
  const analytics = useAnalytics()

  const { data: accountInfo, isLoading: isLoadingAccount, error: accountError } = useAccountInfoQuery()

  // Rozstrzygnięcie tożsamości = koniec pierwszego zapytania o profil, bez względu
  // na wynik (200 z profilem albo 401 gościa; retry:false). Dopiero wtedy analityka
  // może ogłosić sesję anonimową — zob. useAnalytics.syncIdentity.
  watch(isLoadingAccount, (loading) => {
    if (!loading) authStore.markResolved()
  }, { immediate: true })

  // Profil i błąd rozstrzygamy w JEDNYM watcherze, nie w dwóch.
  //
  // 401 z /accountinfo to autorytatywne „backend Cię nie zna". Wcześniej reagował na to
  // wyłącznie `if (!info) return` niżej, więc authStore trzymał starego użytkownika:
  // nagłówek pokazywał zalogowanego, checkout widział gościa, a useMetaTracking.context()
  // wciąż rozgłaszał jego `id_customer` do mostu CAPI — i backend wpisywał to id z powrotem
  // do ciasteczka, odtwarzając stan „zombie".
  //
  // Dlaczego jeden watcher: przy błędzie TanStack Query ZACHOWUJE ostatnie udane `data`,
  // więc dwa niezależne watchery potrafiłyby się znosić (jeden czyści, drugi zaraz
  // przywraca), zależnie od kolejności wyzwolenia. Tu pierwszeństwo błędu jest jawne.
  //
  // Tylko 401 — błąd sieci nie jest dowodem na wylogowanie i nie może zrzucać sesji.
  watch([accountInfo, accountError], ([info, err]) => {
    if (err && isSessionExpiredError(err)) {
      authStore.clearUser()
      return
    }
    if (!info) return
    // mapAccountIdentity = JEDNO mapowanie profilu z backendu (wspólne z zapisem
    // „Moje dane"), żeby obiekt `user_data` był identyczny na obu ścieżkach.
    const identity = mapAccountIdentity(info as AccountIdentitySource)
    if (identity) {
      authStore.setUser({
        id: identity.id,
        email: identity.email ?? '',
        firstName: identity.firstName ?? '',
        lastName: identity.lastName ?? '',
        isGuest: Boolean((info as Record<string, unknown>).is_guest),
        newsletter: identity.newsletter,
        birthday: identity.birthday,
        gender: identity.gender,
      })
    }
    else {
      authStore.clearUser()
    }
  }, { immediate: true })

  const loginMutation = useLoginMutation()
  const registerMutation = useRegisterMutation()
  const logoutMutation = useLogoutMutation()
  const passwordResetRequestMutation = usePasswordResetRequestMutation()
  const passwordResetCheckMutation = usePasswordResetCheckMutation()
  const passwordResetEnterMutation = usePasswordResetEnterMutation()

  const loginErrors = ref<string[]>([])
  const registerErrors = ref<string[]>([])

  function sanitizeRedirectTo(redirectTo: string, fallback = '/account/orders'): string {
    if (!redirectTo.startsWith('/') || redirectTo.startsWith('//') || redirectTo.includes('://')) {
      return fallback
    }
    return redirectTo
  }

  async function login(email: string, password: string, redirectTo = '/account/orders') {
    loginErrors.value = []

    if (!email.includes('@')) {
      loginErrors.value.push('Podany adres e-mail jest nieprawidłowy.')
    }
    if (password.length < 8) {
      loginErrors.value.push('Podane hasło jest nieprawidłowe.')
    }
    if (loginErrors.value.length) return false

    try {
      await loginMutation.mutateAsync({ email, password })
      analytics.trackLogin('password')
      try {
        const { shouldShowPushAsk } = usePwa()
        if (shouldShowPushAsk('login')) {
          useUIStore().openPushPermissionSheet('login')
        }
      }
      catch {
        // Never break auth flow
      }
      await router.push(sanitizeRedirectTo(redirectTo))
      return true
    }
    catch {
      loginErrors.value.push('Logowanie nie powiodło się. Sprawdź dane i spróbuj ponownie.')
      return false
    }
  }

  async function register(payload: {
    email: string
    firstName: string
    lastName: string
    password: string
    newsletter: boolean
  }, redirectTo = '/account/orders') {
    registerErrors.value = []

    if (!payload.email.includes('@')) {
      registerErrors.value.push('Wpisz poprawny adres e-mail.')
    }
    if (payload.password.length < 12) {
      registerErrors.value.push('Hasło powinno mieć minimum 12 znaków.')
    }
    if (registerErrors.value.length) return false

    try {
      await registerMutation.mutateAsync(payload)
      analytics.trackSignUp('password')
      try {
        const { shouldShowPushAsk } = usePwa()
        if (shouldShowPushAsk('register')) {
          useUIStore().openPushPermissionSheet('register')
        }
      }
      catch {
        // Never break auth flow
      }
      await router.push(sanitizeRedirectTo(redirectTo))
      return true
    }
    catch {
      registerErrors.value.push('Rejestracja nie powiodła się. Spróbuj ponownie.')
      return false
    }
  }

  async function logout(redirectTo = '/') {
    // Tożsamość gaśnie PRZED żądaniem, nie po nim.
    //
    // Wcześniej `clearUser()` czekał na `mutateAsync()`, a ten w `onSuccess` robi
    // `await queryClient.resetQueries()` — refetche i przerysowania biegły więc jeszcze
    // ze STARYM `currentUser`. W tym okienku useMetaTracking.context() zdążył wysłać
    // `id_customer` do mostu CAPI, a backend wpisywał go z powrotem do świeżo
    // wyczyszczonego ciasteczka — klient wychodził z wylogowania jako „zombie"
    // (id_customer bez wiersza customer_session) i dostawał 401 na całym checkoucie.
    //
    // Token koszyka też leci tutaj: jego własny kontrakt obiecuje zrzut „po złożeniu
    // zamówienia i po wylogowaniu", ale wołał go dotąd wyłącznie useCheckoutCartReset.
    authStore.clearUser()
    clearCartIdentityToken()

    try {
      await logoutMutation.mutateAsync()
    }
    finally {
      // Lokalnie użytkownik jest już wylogowany, więc wychodzimy ze strony konta nawet
      // gdy żądanie padło — inaczej zostałby na widoku, który zaraz i tak zwróci 401.
      await router.push(sanitizeRedirectTo(redirectTo, '/'))
    }
  }

  async function requestPasswordReset(email: string): Promise<void> {
    await passwordResetRequestMutation.mutateAsync(email)
  }

  async function checkPasswordResetToken(email: string, token: string): Promise<boolean> {
    try {
      await passwordResetCheckMutation.mutateAsync({ email, token })
      return true
    }
    catch {
      return false
    }
  }

  async function enterNewPassword(email: string, token: string, password: string, passwordConfirm: string): Promise<void> {
    await passwordResetEnterMutation.mutateAsync({ email, token, password, password_confirm: passwordConfirm })
  }

  function startSocialLogin(provider: 'google' | 'facebook') {
    return navigateTo(`/api/store/auth/social/${provider}/start`, { external: true })
  }

  return {
    accountInfo,
    isLoggedIn: computed(() => authStore.isLoggedIn),
    isLoadingAccount,
    loginErrors,
    registerErrors,
    isLoginPending: computed(() => loginMutation.isPending.value),
    isRegisterPending: computed(() => registerMutation.isPending.value),
    isLogoutPending: computed(() => logoutMutation.isPending.value),
    login,
    register,
    logout,
    requestPasswordReset,
    checkPasswordResetToken,
    enterNewPassword,
    startSocialLogin,
  }
}
